// Adaptador de ANALÍTICA del manager sobre lealify-base (/analytics/*), reemplazo de
// ElasticsearchService/ElasticsearchService2 para los paneles del dueño. Mapea las
// respuestas de base a las formas que ya consumen los paneles (BusinessMetrics, RfmSegments,
// VisitHeatmap, rewardAnalytics, FeedbackMetrics) — sin tocar el render de cada panel.
import { baseApi } from './baseClient';

// ---- Métricas de negocio (Resumen + Salud) ---------------------------------
export interface BusinessMetricsDTO {
  uniqueCustomers: number;
  totalVisits: number;
  totalPointsGiven: number;
  maleCustomers: number;
  femaleCustomers: number;
  unknownGenderCustomers: number;
  zeroVisitClients: number;
  oneVisitClients: number;
  multiVisitClients: number;
  retentionRate: number;
  oneTimeVisitors: number;
  returningClients: number;
  activeRewardsCount: number;
  pointsOutstanding: number;
  lastUpdated: string;
}

// ---- Rendimiento de recompensas (canjes) -----------------------------------
export interface RewardAnalytics {
  totalRedemptions: number;
  totalPointsSpent: number;
  topRewards: Array<{ title: string; count: number; points: number }>;
  trend: Array<{ date: string; count: number }>;
}

// ---- Heatmap (forma que consume VisitsHeatmapPanel) ------------------------
export interface HeatmapCell { dow: number; hour: number; count: number; }
export interface VisitHeatmap {
  cells: HeatmapCell[];
  maxCount: number;
  totalVisits: number;
  timeZone: string;
}

// ---- RFM (forma que consume RfmSegmentsPanel) ------------------------------
export interface ReactivationClient {
  id?: string;
  name: string;
  phone: string;
  countryCode: string;
  days: number;
  points: number;
  visits: number;
}
export interface RfmSegments {
  segments: Record<string, number>;
  baseViva: number;
  historicoInactivo: number;
  totalClientes: number;
  reactivar: ReactivationClient[];
  reactivarIds: string[];
  championIds: string[];
}

// ---- Feedback con timeline (forma que consume FeedbackAnalyticsPanel) -------
export interface FeedbackMetrics {
  overall: {
    totalFeedback: number;
    distribution: { type: string; count: number; percent: number }[];
    npsScore: number;
    averageScore: number;
  };
  timeline: {
    period: string;
    totalFeedback: number;
    distribution: { [key: string]: { count: number; percent: number } };
    averageScore: number;
  }[];
}

// ===========================================================================
// Tipos crudos de base + mapeos
// ===========================================================================

// MySQL DAYOFWEEK: 1=Domingo … 7=Sábado. El panel usa ISO: 1=Lunes … 7=Domingo.
const isoDow = (mysqlDow: number): number => ((mysqlDow + 5) % 7) + 1;

// Claves de segmento de base → etiquetas visibles del panel.
const SEG_LABEL: Record<string, string> = {
  campeones: 'Campeones',
  leales: 'Leales',
  nuevos: 'Nuevos/Prometedores',
  en_riesgo: 'En riesgo',
  dormidos: 'Por dormir',
};

const fmtDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

interface RawHeatmap { days: number; cells: Array<{ dow: number; hour: number; n: number }>; }
interface RawRfmRow {
  id: number; name: string; phone: string | null; points_balance: number;
  recency_days: number | null; frequency: number; monetary: number; segment: string;
}
interface RawReactivationRow { id: number; name: string; phone: string | null; points_balance: number; recency_days: number | null; frequency: number; }
interface RawRfm {
  total: number; base_live: number; historic_inactive: number;
  distribution: Record<string, number>; sample: RawRfmRow[];
  reactivation: { total: number; ids: number[]; items: RawReactivationRow[] };
}

// Feed de visitas insatisfechas (Bloque B — reemplaza ES searchUnsatisfiedClients).
export interface UnsatisfiedVisitRow {
  id: number;
  contact_id: number;
  name: string | null;
  phone: string | null;
  branch_id: number | null;
  branch_name: string | null;
  points_delta: string | number;
  feedback: 'neutral' | 'malo';
  occurred_at: string;
}
export interface UnsatisfiedVisitsResponse {
  total: number;
  page: number;
  size: number;
  items: UnsatisfiedVisitRow[];
}

// ---- Reporte «Por empleado» (GET /loyalty/staff-report, 23-oct-2026) ----------------
export interface StaffMetrics {
  nuevos: number; visitas: number; monto: number; otorgados: number; canjes: number; canjeados: number;
  ajustesMas: number; ptsMas: number; ajustesMenos: number; ptsMenos: number; cancelaciones: number;
}
export interface StaffReport {
  negocio: string; tz: string; desde: string; hasta: string; esCashback: boolean;
  pro: boolean; limitedTo: number | null;
  empleados: { key: string; nombre: string; automatico: boolean; totales: StaffMetrics; porDia: (StaffMetrics & { dia: string })[] }[];
  totales: StaffMetrics;
  porDia: (StaffMetrics & { dia: string })[];
}
const staffQuery = (o: { from: string; to: string; branchId?: string }) => {
  const p = new URLSearchParams({ from: o.from, to: o.to });
  if (o.branchId) p.set('branch_id', o.branchId);
  return p.toString();
};
export const staffReportApi = {
  get: (o: { from: string; to: string; branchId?: string }) => baseApi.get<StaffReport>(`/loyalty/staff-report?${staffQuery(o)}`),
  excel: (o: { from: string; to: string; branchId?: string }) =>
    baseApi.download(`/loyalty/staff-report.xlsx?${staffQuery(o)}`, `por_empleado_${o.from}_a_${o.to}.xlsx`),
};

export const analyticsApi = {
  // Resumen del negocio + salud (useBusinessMetrics).
  businessMetrics(): Promise<BusinessMetricsDTO> {
    return baseApi.get<BusinessMetricsDTO>('/analytics/metrics');
  },

  // Rendimiento de recompensas. days>=10000 = todos los tiempos (igual que el panel ES).
  rewardsPerformance(days: number = 30): Promise<RewardAnalytics> {
    return baseApi.get<RewardAnalytics>(`/analytics/rewards-performance?days=${days}`);
  },

  // Heatmap de horas pico: remapea dow MySQL→ISO y calcula maxCount/totalVisits.
  async heatmap(timeZone?: string, days: number = 180): Promise<VisitHeatmap> {
    const raw = await baseApi.get<RawHeatmap>(`/analytics/heatmap?days=${days}`);
    let maxCount = 0;
    let totalVisits = 0;
    const cells: HeatmapCell[] = (raw.cells || []).map((c) => {
      const count = Number(c.n) || 0;
      if (count > maxCount) maxCount = count;
      totalVisits += count;
      return { dow: isoDow(Number(c.dow) || 0), hour: Number(c.hour) || 0, count };
    });
    return { cells, maxCount, totalVisits, timeZone: timeZone || '' };
  },

  // Segmentos RFM + lista de reactivación. Transforma la salida cruda de base a la
  // forma del panel: normaliza nombres de segmento, deriva baseViva/históricos y arma
  // las listas de campeones y a-reactivar desde la muestra.
  async rfm(): Promise<RfmSegments> {
    const raw = await baseApi.get<RawRfm>('/analytics/rfm?sample=1000');
    const dist = raw.distribution || {};
    const segments: Record<string, number> = {};
    for (const [k, label] of Object.entries(SEG_LABEL)) {
      if (dist[k]) segments[label] = dist[k];
    }
    // baseViva (activos ≤180d) e históricos inactivos los calcula el backend (sobre TODA la
    // población, no la muestra) → la distribución de segmentos suma exactamente baseViva.
    const baseViva = Number(raw.base_live) || 0;
    const historicoInactivo = Number(raw.historic_inactive) || 0;
    const totalClientes = Number(raw.total) || 0;

    const sample = raw.sample || [];
    const championIds = sample.filter((r) => r.segment === 'campeones').map((r) => String(r.id));

    // A reactivar: lista curada del backend (recencia 31-120d + saldo ≥20, orden por puntos).
    // items = top 12 para mostrar; ids = TODOS los que cumplen (la campaña los targetea a todos).
    const reAct = raw.reactivation || { total: 0, ids: [], items: [] };
    const reactivar: ReactivationClient[] = (reAct.items || []).map((r) => ({
      id: String(r.id),
      name: r.name || 'Cliente',
      phone: r.phone || '',
      countryCode: '',
      days: r.recency_days == null ? 0 : Number(r.recency_days),
      points: Number(r.points_balance) || 0,
      visits: Number(r.frequency) || 0,
    }));
    const reactivarIds = (reAct.ids || []).map((id) => String(id));

    return { segments, baseViva, historicoInactivo, totalClientes, reactivar, reactivarIds, championIds };
  },

  // Satisfacción/NPS con timeline. Acepta la firma del panel (businessId ignorado: el
  // tenant lo resuelve la cookie de sesión en base).
  feedbackByPeriod(
    _businessId: string,
    interval: 'day' | 'week' | 'month',
    startDate: Date,
    endDate: Date
  ): Promise<FeedbackMetrics> {
    const qs = `interval=${interval}&from=${fmtDate(startDate)}&to=${fmtDate(endDate)}`;
    return baseApi.get<FeedbackMetrics>(`/analytics/feedback-timeline?${qs}`);
  },

  // Feed paginado de visitas con feedback malo/neutral (useUnsatisfiedClients).
  unsatisfiedVisits(
    opts: { page?: number; size?: number; days?: number; feedback?: Array<'neutral' | 'malo'> } = {}
  ): Promise<UnsatisfiedVisitsResponse> {
    const p = new URLSearchParams();
    p.set('page', String(opts.page ?? 1));
    p.set('size', String(opts.size ?? 20));
    p.set('days', String(opts.days ?? 30));
    if (opts.feedback?.length) p.set('feedback', opts.feedback.join(','));
    return baseApi.get<UnsatisfiedVisitsResponse>(`/analytics/unsatisfied-visits?${p.toString()}`);
  },

  // Telemetría de EXPORTACIÓN (22-ago-2026). El Excel se arma entero en el navegador, así que
  // sin este aviso el servidor no puede saber que alguien exportó — y la decisión pendiente de
  // «¿la exportación pasa a ser PRO?» necesita ese dato. Es best-effort: si falla, se traga el
  // error, porque la descarga del usuario YA ocurrió y no se le va a estropear por telemetría.
  trackExport(pantalla: string): void {
    baseApi.post('/analytics/track-export', { pantalla }).catch(() => {});
  },

  // Bloqueo PRO (2-oct-2026): qué pantalla ve un negocio gratis y en cuál da «Mejorar a PRO».
  // El servidor deja una fila por negocio, pantalla y acción al día. Best-effort, igual que arriba.
  trackPro(feature: string, accion: 'view' | 'click'): void {
    baseApi.post('/analytics/track-pro', { feature, accion }).catch(() => {});
  },
};

export default analyticsApi;
